import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import {
    PIPELINE_CONFIG, TILE_DEFS,
    rotateConnections, drawTileGraphics,
} from './pipelineConfig.js';
import { drawBush, drawRock, drawTree } from '../../ui/Decor.js';

const {
    SINGLE_BLOCK_CHANCE, PROTECTED_MARGIN, OBSTACLE_SHAPES,
    COLOR_GRID_BG, COLOR_GRID_LINE, COLOR_TILE_BG,
    COLOR_START, COLOR_END, COLOR_STATION, COLOR_BLOCKED,
} = PIPELINE_CONFIG;

const STATION_LABEL_STYLE = new TextStyle({
    fontFamily: 'Arial', fontSize: 11, fontWeight: 'bold', fill: '#ffffff',
});

/**
 * Grid board for Track Builder.
 * End building occupies a 2x2 area. Stations are single cells.
 */
export class PipelineBoard {
    container = new Container();

    cols = 0;
    rows = 0;
    cellSize = 0;
    startCol = 0;
    startRow = 0;
    /** Top-left corner of the 2x2 end building */
    endCol = -1;
    endRow = -1;

    /** @type {(null|{type:string,connections:boolean[],passed:boolean,gfx:Graphics})[][]} */
    _grid = [];
    /** @type {boolean[][]} */
    _blocked = [];
    /** @type {Set<string>} "col,row" */
    _stations = new Set();
    /** @type {Set<string>} all 4 cells of the end building */
    _endCells = new Set();
    _hitArea = null;
    _markerOverlay = null;
    _themeManager = null;

    constructor(cols, rows, cellSize, themeManager = null) {
        this.cols = cols;
        this.rows = rows;
        this.cellSize = cellSize;
        this._themeManager = themeManager;
        this.container.sortableChildren = true;
    }

    /**
     * @param {number} startCol
     * @param {number} startRow
     * @param {number} endCol  — top-left col of 2x2 end building
     * @param {number} endRow  — top-left row of 2x2 end building
     * @param {{col:number,row:number}[]} stations
     */
    init(startCol, startRow, endCol, endRow, stations) {
        this.startCol = startCol;
        this.startRow = startRow;
        this.endCol = endCol;
        this.endRow = endRow;
        this._stations = new Set(stations.map(s => `${s.col},${s.row}`));

        this._endCells = new Set();
        for (let dc = 0; dc < 2; dc++) {
            for (let dr = 0; dr < 2; dr++) {
                this._endCells.add(`${endCol + dc},${endRow + dr}`);
            }
        }

        this._grid = Array.from({ length: this.rows },
            () => new Array(this.cols).fill(null));
        this._blocked = Array.from({ length: this.rows },
            () => new Array(this.cols).fill(false));

        this._generateObstacles();
        this._drawBackground();

        this._markerOverlay = new Container();
        this.container.addChild(this._markerOverlay);
        this._drawMarkers();

        this._drawStartMarker();

        const startConns = rotateConnections(TILE_DEFS.straight, 1);
        this._placeInternal(startCol, startRow, 'straight', startConns);
    }

    placeTile(col, row, type, rotation) {
        if (!this._inBounds(col, row)) return false;
        if (this._blocked[row][col]) return false;
        if (this._grid[row][col] !== null) return false;

        const connections = rotateConnections(TILE_DEFS[type], rotation);
        this._placeInternal(col, row, type, connections);
        return true;
    }

    rotateTile(col, row) {
        if (!this._inBounds(col, row)) return false;
        const tile = this._grid[row][col];
        if (!tile || tile.passed) return false;
        if (col === this.startCol && row === this.startRow) return false;

        const [n, e, s, w] = tile.connections;
        tile.connections = [w, n, e, s];

        this._drawTileDisplay(tile.gfx, tile.type, tile.connections, false);
        return true;
    }

    getTile(col, row) {
        if (!this._inBounds(col, row)) return null;
        return this._grid[row][col];
    }

    isEmpty(col, row) {
        if (!this._inBounds(col, row)) return false;
        return !this._blocked[row][col] && this._grid[row][col] === null;
    }

    isBlocked(col, row) {
        if (!this._inBounds(col, row)) return true;
        return this._blocked[row][col];
    }

    isStation(col, row) {
        return this._stations.has(`${col},${row}`);
    }

    isEnd(col, row) {
        return this._endCells.has(`${col},${row}`);
    }

    /**
     * Mark a tile as passed. Returns flags for special cells.
     * @returns {{ station: boolean, end: boolean }}
     */
    markPassed(col, row) {
        const tile = this.getTile(col, row);
        const wasAlreadyPassed = tile?.passed;
        if (tile && !tile.passed) {
            tile.passed = true;
            this._drawTileDisplay(tile.gfx, tile.type, tile.connections, true);
        }
        return {
            station: !wasAlreadyPassed && this.isStation(col, row),
            end: !wasAlreadyPassed && this.isEnd(col, row),
        };
    }

    getHitArea() {
        return this._hitArea;
    }

    destroy() {
        this.container.removeChildren();
        this._grid = [];
        this._blocked = [];
        this._stations.clear();
        this._endCells.clear();
    }

    // ── internals ──────────────────────────────────────

    _inBounds(col, row) {
        return col >= 0 && col < this.cols && row >= 0 && row < this.rows;
    }

    _placeInternal(col, row, type, connections) {
        const cs = this.cellSize;
        const gfx = new Graphics();
        gfx.x = col * cs;
        gfx.y = row * cs;
        gfx.zIndex = 20;
        this._drawTileDisplay(gfx, type, connections, false);
        this._grid[row][col] = { type, connections, passed: false, gfx };
        this.container.addChild(gfx);

        if (this._markerOverlay) {
            this.container.removeChild(this._markerOverlay);
            this.container.addChild(this._markerOverlay);
        }
    }

    _drawTileDisplay(target, type, connections, passed) {
        target.clear();
        target.removeChildren();

        const slotKey = this._getTrackSlotKey(type, connections);
        if (slotKey) {
            const sprite = this._themeManager?.createSprite(slotKey, this.cellSize - 2, this.cellSize - 2, {
                fitMode: 'contain',
            });
            if (sprite) {
                sprite.x = this.cellSize / 2;
                sprite.y = this.cellSize / 2;
                sprite.rotation = this._getTrackRotation(type, connections);
                if (passed) sprite.alpha = 0.78;
                target.roundRect(1, 1, this.cellSize - 2, this.cellSize - 2, 4);
                target.fill(COLOR_TILE_BG);
                target.addChild(sprite);
                return;
            }
        }

        drawTileGraphics(target, this.cellSize, connections, passed, COLOR_TILE_BG);
    }

    _getTrackSlotKey(type, connections) {
        if (type === 'straight') return 'pipeline.track.straight';
        if (type === 'curve') return 'pipeline.track.corner';
        if (type === 'cross') return 'pipeline.track.cross';

        const activeCount = connections.filter(Boolean).length;
        if (activeCount === 3) return 'pipeline.track.t';
        if (activeCount === 1) return 'pipeline.track.deadend';
        return null;
    }

    _getTrackRotation(type, connections) {
        const [n, e, s, w] = connections;
        if (type === 'straight') {
            return n && s ? Math.PI / 2 : 0;
        }
        if (type === 'curve') {
            if (e && s) return Math.PI / 2;
            if (s && w) return Math.PI;
            if (w && n) return Math.PI * 1.5;
            return 0;
        }
        if (type !== 'cross') {
            if (e && s && w) return Math.PI / 2;
            if (s && w && n) return Math.PI;
            if (w && n && e) return Math.PI * 1.5;
            if (e) return Math.PI / 2;
            if (s) return Math.PI;
            if (w) return Math.PI * 1.5;
        }
        return 0;
    }

    _isProtected(c, r) {
        const m = PROTECTED_MARGIN;
        if (Math.abs(c - this.startCol) <= m && Math.abs(r - this.startRow) <= m) return true;

        for (let dc = 0; dc < 2; dc++) {
            for (let dr = 0; dr < 2; dr++) {
                if (Math.abs(c - (this.endCol + dc)) <= 1 &&
                    Math.abs(r - (this.endRow + dr)) <= 1) return true;
            }
        }

        for (const key of this._stations) {
            const [sc, sr] = key.split(',').map(Number);
            if (Math.abs(c - sc) <= 1 && Math.abs(r - sr) <= 1) return true;
        }
        return false;
    }

    _generateObstacles() {
        const { cols, rows } = this;

        for (const shape of OBSTACLE_SHAPES) {
            for (let n = 0; n < shape.count; n++) {
                for (let att = 0; att < 40; att++) {
                    const c = Math.floor(Math.random() * (cols - shape.w + 1));
                    const r = Math.floor(Math.random() * (rows - shape.h + 1));

                    let fits = true;
                    for (let dc = 0; dc < shape.w && fits; dc++) {
                        for (let dr = 0; dr < shape.h && fits; dr++) {
                            if (this._isProtected(c + dc, r + dr) ||
                                this._blocked[r + dr][c + dc]) {
                                fits = false;
                            }
                        }
                    }
                    if (!fits) continue;

                    for (let dc = 0; dc < shape.w; dc++) {
                        for (let dr = 0; dr < shape.h; dr++) {
                            this._blocked[r + dr][c + dc] = true;
                        }
                    }
                    break;
                }
            }
        }

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                if (this._blocked[r][c] || this._isProtected(c, r)) continue;
                if (Math.random() < SINGLE_BLOCK_CHANCE) {
                    this._blocked[r][c] = true;
                }
            }
        }
    }

    _drawBackground() {
        const { cols, rows, cellSize: cs } = this;
        const gridW = cols * cs;
        const gridH = rows * cs;
        const bg = new Graphics();
        const hasThemeBg = Boolean(this._themeManager?.get('pipeline.background'));

        bg.rect(0, 0, gridW, gridH);
        bg.fill(hasThemeBg ? { color: COLOR_GRID_BG, alpha: 0.82 } : COLOR_GRID_BG);

        for (let c = 1; c < cols; c++) {
            bg.rect(c * cs, 0, 1, gridH); bg.fill(COLOR_GRID_LINE);
        }
        for (let r = 1; r < rows; r++) {
            bg.rect(0, r * cs, gridW, 1); bg.fill(COLOR_GRID_LINE);
        }

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                if (this._blocked[r][c]) {
                    this._drawObstacleCell(bg, c, r, cs);
                }
            }
        }

        for (const key of this._stations) {
            const [sc, sr] = key.split(',').map(Number);
            bg.roundRect(sc * cs + 2, sr * cs + 2, cs - 4, cs - 4, 6);
            bg.stroke({ color: COLOR_STATION, width: 3 });
        }

        const ex = this.endCol * cs;
        const ey = this.endRow * cs;
        bg.roundRect(ex + 2, ey + 2, cs * 2 - 4, cs * 2 - 4, 6);
        bg.stroke({ color: COLOR_END, width: 3 });

        this.container.addChildAt(bg, 0);
        bg.zIndex = 0;

        this._hitArea = new Graphics();
        this._hitArea.rect(0, 0, gridW, gridH);
        this._hitArea.fill({ color: 0x000000, alpha: 0.001 });
        this._hitArea.eventMode = 'static';
        this._hitArea.cursor = 'pointer';
        this._hitArea.zIndex = 5;
        this.container.addChild(this._hitArea);
    }

    _drawObstacleCell(g, col, row, cs) {
        const x = col * cs;
        const y = row * cs;
        const kind = (col * 17 + row * 31) % 3;
        const blockedSprite = this._themeManager?.createSprite('pipeline.blocked', cs - 8, cs - 8, { fitMode: 'cover' });
        if (blockedSprite) {
            blockedSprite.x = x + cs / 2;
            blockedSprite.y = y + cs / 2;
            g.addChild(blockedSprite);
            return;
        }

        g.roundRect(x + 4, y + 4, cs - 8, cs - 8, 10);
        g.fill({ color: COLOR_BLOCKED, alpha: 0.88 });
        g.stroke({ color: 0x334155, width: 2 });

        if (kind === 0) {
            drawTree(g, x, y, cs, cs, { shadow: false });
        } else if (kind === 1) {
            drawBush(g, x, y, cs, cs, { shadow: false });
        } else {
            drawRock(g, x, y, cs, cs);
        }
    }

    _drawMarkers() {
        const cs = this.cellSize;

        for (const key of this._stations) {
            const [sc, sr] = key.split(',').map(Number);
            const stationSprite = this._themeManager?.createSprite('pipeline.station', cs, cs, { fitMode: 'contain' });
            if (stationSprite) {
                stationSprite.x = sc * cs + cs / 2;
                stationSprite.y = sr * cs + cs / 2;
                this._markerOverlay.addChild(stationSprite);
                continue;
            }
            const g = new Graphics();
            const cx = sc * cs + cs / 2;
            const cy = sr * cs + cs / 2;
            const r = cs * 0.22;

            g.circle(cx, cy, r);
            g.fill({ color: COLOR_STATION, alpha: 0.5 });
            g.stroke({ color: COLOR_STATION, width: 2 });

            const label = new Text({ text: 'S', style: STATION_LABEL_STYLE });
            label.anchor.set(0.5);
            label.x = cx;
            label.y = cy;

            this._markerOverlay.addChild(g);
            this._markerOverlay.addChild(label);
        }

        const ex = this.endCol * cs;
        const ey = this.endRow * cs;
        const bw = cs * 2;
        const bh = cs * 2;
        const goalSprite = this._themeManager?.createSprite('pipeline.goal', bw, bh, { fitMode: 'cover' });
        if (goalSprite) {
            goalSprite.x = ex + bw / 2;
            goalSprite.y = ey + bh / 2;
            this._markerOverlay.addChild(goalSprite);
            return;
        }
        const g = new Graphics();

        g.rect(ex + bw * 0.15, ey + bh * 0.35, bw * 0.7, bh * 0.58);
        g.fill({ color: COLOR_END, alpha: 0.6 });

        g.moveTo(ex + bw * 0.05, ey + bh * 0.38);
        g.lineTo(ex + bw * 0.5, ey + bh * 0.08);
        g.lineTo(ex + bw * 0.95, ey + bh * 0.38);
        g.closePath();
        g.fill({ color: 0xfca5a5, alpha: 0.8 });

        g.rect(ex + bw * 0.38, ey + bh * 0.6, bw * 0.24, bh * 0.33);
        g.fill(0x78350f);

        g.rect(ex + bw * 0.2, ey + bh * 0.42, bw * 0.14, bh * 0.14);
        g.fill(0x93c5fd);
        g.rect(ex + bw * 0.66, ey + bh * 0.42, bw * 0.14, bh * 0.14);
        g.fill(0x93c5fd);

        this._markerOverlay.addChild(g);

        const endLabel = new Text({
            text: 'END',
            style: new TextStyle({
                fontFamily: 'Arial', fontSize: 14, fontWeight: 'bold', fill: '#ffffff',
            }),
        });
        endLabel.anchor.set(0.5);
        endLabel.x = ex + bw / 2;
        endLabel.y = ey + bh * 0.25;
        this._markerOverlay.addChild(endLabel);
    }

    _drawStartMarker() {
        const cs = this.cellSize;
        const x = this.startCol * cs;
        const y = this.startRow * cs;
        const startSprite = this._themeManager?.createSprite('pipeline.start', cs, cs, { fitMode: 'contain' });
        if (startSprite) {
            startSprite.x = x + cs / 2;
            startSprite.y = y + cs / 2;
            this.container.addChild(startSprite);
            return;
        }
        const g = new Graphics();

        g.rect(x, y, cs, cs);
        g.stroke({ color: COLOR_START, width: 3 });

        const cy = y + cs / 2;
        const a = 10;
        g.moveTo(x - a - 4, cy - a);
        g.lineTo(x - 4, cy);
        g.lineTo(x - a - 4, cy + a);
        g.closePath();
        g.fill(COLOR_START);

        this.container.addChild(g);
    }
}
